import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Plus, Upload, Edit, Trash2, FileText, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';

const KnowledgeBaseView = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newDoc, setNewDoc] = useState({
    title: '',
    content: '',
    assigned_features: [],
    tags: []
  });
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  const features = [
    { id: 'affirmations', label: 'Affirmations' },
    { id: 'journaling', label: 'Journaling' },
    { id: 'companion', label: 'Conchi' },
    { id: 'divine_guidance', label: 'Divine Guidance' }
  ];

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/knowledge-base`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch docs');

      const data = await response.json();
      setDocs(data.data || []);
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDoc = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/knowledge-base`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newDoc)
      });

      if (!response.ok) throw new Error('Failed to create doc');

      toast({
        title: '✅ Success',
        description: 'Knowledge base document created',
        variant: 'default'
      });

      setShowUploadModal(false);
      setNewDoc({ title: '', content: '', assigned_features: [], tags: [] });
      fetchDocs();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleToggleActive = async (docId, currentStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/knowledge-base/${docId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to update doc');

      toast({
        title: '✅ Updated',
        description: `Document ${!currentStatus ? 'activated' : 'deactivated'}`,
        variant: 'default'
      });

      fetchDocs();
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
            Knowledge Base
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Upload reference docs to ground AI responses in your business knowledge
          </p>
        </div>
        <Button
          onClick={() => setShowUploadModal(true)}
          className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Document
        </Button>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No knowledge base documents yet</p>
          <p className="text-sm text-gray-500 mt-2">Upload reference docs to improve AI accuracy</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {docs.map(doc => (
            <Card key={doc.id} className="p-6 hover:shadow-lg transition-all border-2 border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-pink-600" />
                    <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                    {doc.is_active ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>

                  {/* Assigned Features */}
                  {doc.assigned_features && doc.assigned_features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {doc.assigned_features.map((feature, i) => (
                        <span key={i} className="px-2 py-1 bg-pink-100 text-pink-800 rounded text-xs">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Content Preview */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {doc.content}
                  </p>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div>Size: <span className="font-medium">{formatSize(doc.content_size)}</span></div>
                    <div>Status: <span className="font-medium">{doc.indexing_status}</span></div>
                    <div>Updated: {new Date(doc.updated_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(doc.id, doc.is_active)}
                  >
                    {doc.is_active ? (
                      <ToggleRight className="w-4 h-4" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 m-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add Knowledge Base Document</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Title
                </label>
                <input
                  type="text"
                  value={newDoc.title}
                  onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                  placeholder="e.g., NeuroLove Method Guidelines"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={newDoc.content}
                  onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                  rows={10}
                  placeholder="Paste your reference document content here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              {/* Assign to Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to Features
                </label>
                <div className="flex flex-wrap gap-2">
                  {features.map(feature => (
                    <button
                      key={feature.id}
                      onClick={() => {
                        const current = newDoc.assigned_features || [];
                        if (current.includes(feature.id)) {
                          setNewDoc({
                            ...newDoc,
                            assigned_features: current.filter(f => f !== feature.id)
                          });
                        } else {
                          setNewDoc({
                            ...newDoc,
                            assigned_features: [...current, feature.id]
                          });
                        }
                      }}
                      className={`px-3 py-2 rounded-lg font-medium transition-all ${
                        (newDoc.assigned_features || []).includes(feature.id)
                          ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white'
                          : 'bg-white text-gray-600 border border-gray-200'
                      }`}
                    >
                      {feature.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowUploadModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateDoc}
                  disabled={!newDoc.title || !newDoc.content}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Create Document
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseView;